import { useState } from 'react';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../context/ToastContext.jsx';
import {
  getMembershipLevels, adminCreateLevel, adminUpdateLevel, adminDeleteLevel,
} from '../../api/membershipLevelsApi.js';

function AdminMembershipLevelsPage() {
  const { showToast } = useToast();
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', data }

  const { data: levels = [], loading } = useApi(() => getMembershipLevels(), [reloadKey]);
  const reload = () => setReloadKey(k => k + 1);

  const handleDelete = async (lv) => {
    if (lv.level === 0) return;
    if (!window.confirm(`確定刪除等級「${lv.name}」？\n（仍有會員是此等級時會被擋下）`)) return;
    try {
      await adminDeleteLevel(lv.id);
      showToast(`已刪除等級：${lv.name}`, 'success');
      reload();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error');
    }
  };

  const handleSave = async (form) => {
    try {
      if (modal.mode === 'create') {
        await adminCreateLevel({ level: form.level, name: form.name });
        showToast('等級已建立', 'success');
      } else {
        await adminUpdateLevel(modal.data.id, { name: form.name });
        showToast('等級已更新', 'success');
      }
      setModal(null);
      reload();
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    }
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>會員等級<small>MEMBERSHIP LEVELS</small></h1>
      </div>

      <div className="admin-card" style={{ marginBottom: 16 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 13, lineHeight: 1.7 }}>
          等級數字越大越高（門檻制）。商品可設「最低可見等級」，會員等級 ≥ 該門檻才看得到。
          要把某位會員升級，請到 <b style={{ color: 'var(--navy)' }}>「使用者管理」</b> 調整。
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="admin-btn primary" onClick={() => setModal({ mode: 'create', data: { level: '', name: '' } })}>
          + 新增等級
        </button>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 120 }}>等級數字</th>
              <th>名稱</th>
              <th style={{ width: 140 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={3} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : levels.length === 0
                ? <tr><td colSpan={3} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>尚無等級</td></tr>
                : levels.map(lv => (
                  <tr key={lv.id}>
                    <td style={{ font: '700 14px monospace', color: 'var(--navy)' }}>{lv.level}</td>
                    <td style={{ fontSize: 14 }}>
                      {lv.name}
                      {lv.level === 0 && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink-soft)', border: '1px solid var(--line, #dfe3ec)', borderRadius: 4, padding: '1px 6px' }}>預設</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="admin-btn ghost sm" onClick={() => setModal({ mode: 'edit', data: lv })}>編輯</button>
                        <button
                          className="admin-btn danger sm"
                          disabled={lv.level === 0}
                          title={lv.level === 0 ? '預設等級不可刪除' : ''}
                          onClick={() => handleDelete(lv)}
                        >刪除</button>
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {modal && (
        <LevelModal mode={modal.mode} initial={modal.data} onSave={handleSave} onClose={() => setModal(null)} />
      )}
    </>
  );
}

function LevelModal({ mode, initial, onSave, onClose }) {
  const [form, setForm] = useState({
    level: initial.level ?? '',
    name: initial.name || '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (mode === 'create' && (form.level === '' || Number(form.level) < 0)) return;
    setSubmitting(true);
    try {
      await onSave({ level: Number(form.level), name: form.name.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHead}>
          <h2 style={styles.modalTitle}>{mode === 'create' ? '新增等級' : '編輯等級'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>等級數字（建立後不可改）{mode === 'create' ? ' *' : ''}</label>
            <input
              type="number"
              min={0}
              step={1}
              value={form.level}
              onChange={e => setForm(f => ({ ...f, level: e.target.value }))}
              placeholder="3"
              disabled={mode === 'edit'}
              style={styles.input}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>名稱 *</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="VIP3"
              style={styles.input}
            />
          </div>
          <div style={styles.actions}>
            <button type="button" className="admin-btn ghost" onClick={onClose}>取消</button>
            <button type="submit" className="admin-btn primary" disabled={submitting}>
              {submitting ? '儲存中…' : '儲存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(14, 28, 64, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'var(--white)', borderRadius: 14,
    padding: '28px 32px', width: '90%', maxWidth: 460,
    boxShadow: '0 8px 40px rgba(14,28,64,0.18)',
  },
  modalHead: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 24,
  },
  modalTitle: { font: '800 20px "Noto Sans TC", sans-serif', color: 'var(--navy)', margin: 0 },
  closeBtn: {
    background: 'none', border: 'none', font: '600 18px Inter',
    color: 'var(--ink-soft)', cursor: 'pointer', padding: '4px 8px',
  },
  field: { marginBottom: 16 },
  label: { display: 'block', font: '600 13px "Noto Sans TC"', color: 'var(--navy)', marginBottom: 6 },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1px solid var(--line, #dfe3ec)', borderRadius: 8,
    font: '400 14px "Noto Sans TC"', boxSizing: 'border-box',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 },
};

export default AdminMembershipLevelsPage;
